import {
  Bookmark,
  FilterX,
  Inbox,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useMe } from "../auth/session";
import { firstName, greeting } from "../lib/format";
import { useDebouncedValue, usePageTitle, useStickyHead } from "../lib/hooks";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/Field";
import { EmptyState, ErrorState, Notice, errorMessage } from "../ui/Notice";
import { Skeleton, SkeletonRegion } from "../ui/Skeleton";
import { Spinner } from "../ui/Spinner";
import {
  useDeleteFilter,
  useSavedFilters,
  useSaveFilter,
  useTickets,
} from "../tickets/api";
import { Select } from "../ui/Select";
import { PeekPane, useTicketPeek } from "../tickets/peek";
import { TicketTable } from "../tickets/TicketTable";
import { emptyFilters, filterQuery, isFiltered, useColumnFilters, type FilterView } from "../tickets/columnFilters";
import "../tickets/tickets.css";
import "./home.css";

const PAGE_SIZE = 50;
// The Tickets page starts on open tickets.
const START = emptyFilters("open");

/** A saved view (including ones saved when the page had queue tabs) as filters. */
function asView(filters: Record<string, unknown>): FilterView {
  const view = { ...START };
  for (const key of Object.keys(START) as (keyof FilterView)[]) {
    if (typeof filters[key] === "string") (view as Record<string, string>)[key] = filters[key];
  }
  if (!view.status) view.status = "open";
  if (filters.queue === "mine") view.owner = "me";
  else if (filters.queue === "unassigned") view.owner = "none";
  else if (filters.queue === "overdue") view.due = "overdue";
  else if (filters.queue === "raised") view.requester = "me";
  if (filters.show === "done") view.status = "done";
  else if (filters.show === "all") view.status = "any";
  return view;
}

/** Tickets for staff: every ticket in one table, with a filter on each column. */
export function WorkspaceHomePage() {
  usePageTitle("Tickets");
  const me = useMe();
  // Links can open the page on your own tickets (?queue=raised), e.g. "View all" on Help.
  const [params] = useSearchParams();
  const [view, setView] = useState<FilterView>(() =>
    asView({ queue: params.get("queue") }),
  );
  const [offset, setOffset] = useState(0);
  const [savedId, setSavedId] = useState("");
  const [saving, setSaving] = useState(false);
  const q = useDebouncedValue(view.q.trim(), 250);
  const saved = useSavedFilters();
  const deleteFilter = useDeleteFilter();

  const tickets = useTickets({ ...filterQuery(view, me.id, q), limit: PAGE_SIZE, offset });
  const items = tickets.data?.items ?? [];
  const total = tickets.data?.total ?? 0;

  function update(patch: Partial<FilterView>) {
    setView((current) => ({ ...current, ...patch }));
    setOffset(0);
    setSavedId("");
  }

  const filtered = isFiltered(view, START);
  function clearFilters() {
    update({ ...START });
  }

  const filters = useColumnFilters(view, update, START);
  const peek = useTicketPeek(items);
  const head = useStickyHead<HTMLDivElement>();

  return (
    <div className="tickets-page">
      {/* Title, actions and count stay at the top while the list scrolls under them. */}
      <div className="page-head-sticky" ref={head}>
      {/* The greeting banner is the page's header: it stays with the count. */}
      <section className="card card-hero home-hero tickets-hero" aria-labelledby="tickets-title">
        <div className="home-hero-text">
          <h1 id="tickets-title" className="home-hero-title">
            {greeting()}, {firstName(me.display_name)}
          </h1>
          <p className="home-hero-lead">
            {me.groups.length ? `Your groups: ${me.groups.map((g) => g.name).join(", ")}` : "All tickets across the desk."}
          </p>
        </div>
        <div className="home-hero-actions tickets-hero-actions">
            <Link to="/tickets/new" className="btn btn-hero">
              <Plus size={18} aria-hidden="true" />
              Raise a ticket
            </Link>
            <Select
              label="Saved views"
              className="saved-select"
              value={savedId}
              placeholder={saved.data?.length ? "Saved views…" : "No saved views yet"}
              onChange={(id) => {
                const filter = saved.data?.find((f) => f.id === id);
                if (filter) {
                  setView(asView(filter.filters));
                  setOffset(0);
                }
                setSavedId(id);
              }}
              groups={[{ options: (saved.data ?? []).map((f) => ({ value: f.id, label: f.name })) }]}
            />
            {savedId ? (
              <button
                type="button"
                className="btn btn-hero-ghost btn-icon"
                aria-label="Delete this saved view"
                onClick={() =>
                  deleteFilter.mutate(savedId, {
                    onSuccess: () => setSavedId(""),
                  })
                }
              >
                <Trash2 size={18} aria-hidden="true" />
              </button>
            ) : null}
            <button type="button" className="btn btn-hero-ghost" onClick={() => setSaving(true)}>
              <Bookmark size={18} aria-hidden="true" />
              Save view
            </button>
        </div>
      </section>


      <div className="table-meta">
        {filtered ? (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            <FilterX size={16} aria-hidden="true" />
            Clear filters
          </Button>
        ) : null}
        <p className="toolbar-meta" aria-live="polite">
          {tickets.isFetching && !tickets.isPending ? <Spinner /> : null}
          {tickets.data ? (
            <b>{total === 1 ? "1 ticket" : `${total} tickets`}</b>
          ) : null}
        </p>
      </div>
      </div>

      <div className={peek.openRef ? "peek-layout" : "peek-layout-closed"}>
        <div className="list-column">
        <section className="card table-card" aria-label="Tickets">
          {tickets.isPending ? (
            <SkeletonRegion label="Loading tickets" className="table-skeleton">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="table-skeleton-row">
                  <span className="skeleton-stack">
                    <Skeleton width="55%" />
                    <Skeleton width="30%" />
                  </span>
                  <Skeleton width="5rem" />
                  <Skeleton width="4.5rem" />
                  <Skeleton width="7rem" />
                </div>
              ))}
            </SkeletonRegion>
          ) : tickets.isError ? (
            <ErrorState
              error={tickets.error}
              onRetry={() => void tickets.refetch()}
            />
          ) : (
            <>
              {/* The headings carry the filters, so the table stays even when nothing matches. */}
              <TicketTable
                tickets={items}
                filters={filters}
                onOpen={peek.open}
                openRef={peek.openRef}
                activeRef={peek.activeRef}
                compact={Boolean(peek.openRef)}
              />
              {items.length === 0 ? (
                <QueueEmpty filtered={filtered} onClear={clearFilters} />
              ) : null}
              {total > PAGE_SIZE ? (
                <div className="pagination">
                  <span>
                    {offset + 1}–{offset + items.length} of {total}
                  </span>
                  <span className="row">
                    <Button
                      size="sm"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      disabled={offset + PAGE_SIZE >= total}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </span>
                </div>
              ) : null}
            </>
          )}
        </section>
        </div>
        {peek.openRef ? <PeekPane reference={peek.openRef} onClose={peek.close} /> : null}
      </div>

      {saving ? (
        <SaveViewDialog
          view={view}
          onClose={() => setSaving(false)}
          onSaved={setSavedId}
        />
      ) : null}
    </div>
  );
}

function SaveViewDialog({
  view,
  onClose,
  onSaved,
}: {
  view: FilterView;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const save = useSaveFilter();
  return (
    <Dialog
      open
      title="Save this view"
      onClose={onClose}
      onSubmit={() => {
        if (!name.trim()) return;
        save.mutate(
          { name: name.trim(), filters: { ...view } },
          {
            onSuccess: (saved) => {
              onSaved(saved.id);
              onClose();
            },
          },
        );
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Save view
          </Button>
        </>
      }
    >
      <TextField
        label="Name"
        hint="Saving with an existing name updates that view."
        value={name}
        maxLength={80}
        autoFocus
        onChange={(event) => setName(event.target.value)}
      />
      {save.isError ? (
        <Notice tone="error">{errorMessage(save.error)}</Notice>
      ) : null}
    </Dialog>
  );
}

function QueueEmpty({
  filtered,
  onClear,
}: {
  filtered: boolean;
  onClear: () => void;
}) {
  if (filtered) {
    return (
      <EmptyState
        icon={<FilterX size={24} />}
        title="No tickets match these filters"
        action={
          <Button variant="primary" onClick={onClear}>
            Clear filters
          </Button>
        }
      >
        Try another search or clear the filters.
      </EmptyState>
    );
  }
  return (
    <EmptyState icon={<Inbox size={24} />} title="No open tickets">
      You're all caught up.
    </EmptyState>
  );
}
